import {Transaction} from "../../domain/transaction.entity";
import {
  PaginatedResult,
  TransactionFilterParams,
  TransactionExportParams,
} from "../../domain/transaction.types";
import {httpClientV2} from "../../../../infrastructure/http";
import {TransactionMapperV2} from "../mappers/transaction.mapper.v2";
import {
  V2LocalTransactionsListResponse,
  V2ForeignTransactionsListResponse,
} from "../mappers/transaction.mapper.types";
import {TransactionRepository} from "./transaction.repository";

/**
 * v2 API implementation of TransactionRepository.
 *
 * v2 API differences from v1:
 * - Base path: /payments/analytics/transactions/
 * - Uses `perPage` instead of `limit` in query params
 * - Uses `searchParams` to search both offering title and customer name
 * - Same endpoints for listing and filtering (just add query params to filter)
 *
 * Pure TypeScript - no React imports.
 */
export class TransactionRepositoryV2 implements TransactionRepository {
  private readonly basePath = "payments/analytics/transactions";

  async getLocalTransactions(params: {
    page: number;
    limit: number;
  }): Promise<PaginatedResult<Transaction>> {
    const response = await httpClientV2.get<V2LocalTransactionsListResponse>(
      `${this.basePath}/local?page=${params.page}&perPage=${params.limit}`
    );

    return {
      data: TransactionMapperV2.toDomainList(response.data.data, "local"),
      meta: TransactionMapperV2.mapPaginationMeta({
        ...response.data,
        currentPage: params.page,
        perPage: params.limit,
      }),
    };
  }

  async getForeignTransactions(params: {
    page: number;
    limit: number;
  }): Promise<PaginatedResult<Transaction>> {
    const response = await httpClientV2.get<V2ForeignTransactionsListResponse>(
      `${this.basePath}/fx?page=${params.page}&perPage=${params.limit}`
    );

    return {
      data: TransactionMapperV2.toDomainList(response.data.data, "foreign"),
      meta: TransactionMapperV2.mapPaginationMeta({
        ...response.data,
        currentPage: params.page,
        perPage: params.limit,
      }),
    };
  }

  async filterLocalTransactions(
    params: TransactionFilterParams
  ): Promise<PaginatedResult<Transaction>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 10;

    const queryString = this.buildFilterQueryString(params, page, limit);
    const response = await httpClientV2.get<V2LocalTransactionsListResponse>(
      `${this.basePath}/local${queryString}`
    );

    return {
      data: TransactionMapperV2.toDomainList(response.data.data, "local"),
      meta: TransactionMapperV2.mapPaginationMeta({
        ...response.data,
        currentPage: page,
        perPage: limit,
      }),
    };
  }

  async filterForeignTransactions(
    params: TransactionFilterParams
  ): Promise<PaginatedResult<Transaction>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 10;

    const queryString = this.buildFilterQueryString(params, page, limit);
    const response = await httpClientV2.get<V2ForeignTransactionsListResponse>(
      `${this.basePath}/fx${queryString}`
    );

    return {
      data: TransactionMapperV2.toDomainList(response.data.data, "foreign"),
      meta: TransactionMapperV2.mapPaginationMeta({
        ...response.data,
        currentPage: page,
        perPage: limit,
      }),
    };
  }

  async exportTransactionsCSV(params: TransactionExportParams): Promise<unknown> {
    const endpoint =
      params.transactionType === "local"
        ? `${this.basePath}/local/export?startDate=${params.startDate}&endDate=${params.endDate}`
        : `${this.basePath}/fx/export?startDate=${params.startDate}&endDate=${params.endDate}`;

    return httpClientV2.get(endpoint);
  }

  /**
   * Build query string for v2 search endpoints.
   * v2 uses searchParams to search both offering title and customer name.
   */
  private buildFilterQueryString(
    params: TransactionFilterParams,
    page: number,
    perPage: number
  ): string {
    const queryParams = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
    });

    if (params.startDate) {
      queryParams.set("startDate", params.startDate);
    }

    if (params.endDate) {
      queryParams.set("endDate", params.endDate);
    }

    if (params.searchParams) {
      queryParams.set("searchParams", params.searchParams);
    }

    return `?${queryParams.toString()}`;
  }
}
